import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetSpecialOfferQuery,
  useGetHomepageSettingsQuery,
} from "../redux/services/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useTranslation } from "../hooks/useTranslation";
import { trackOfferPage } from "../utils/analytics";
import AOS from "aos";
import "aos/dist/aos.css";

// Helper function to parse Draft.js content to HTML
const parseDraftContent = (content) => {
  if (!content) return "";

  try {
    // If content is already a string, return it
    if (typeof content === "string") {
      // Check if it's JSON string
      if (content.startsWith("{") && content.endsWith("}")) {
        const parsed = JSON.parse(content);
        return parseDraftBlocks(parsed);
      }
      return content;
    }

    // If content is an object, parse it directly
    if (typeof content === "object") {
      return parseDraftBlocks(content);
    }

    return content;
  } catch (error) {
    console.error("Error parsing content:", error);
    return content;
  }
};

const parseDraftBlocks = (draftContent) => {
  if (!draftContent.blocks || !Array.isArray(draftContent.blocks)) {
    return "";
  }

  return draftContent.blocks
    .map((block) => {
      if (block.type === "unstyled") {
        return `<p>${block.text}</p>`;
      } else if (block.type === "header-one") {
        return `<h1>${block.text}</h1>`;
      } else if (block.type === "header-two") {
        return `<h2>${block.text}</h2>`;
      } else if (block.type === "header-three") {
        return `<h3>${block.text}</h3>`;
      } else if (block.type === "unordered-list-item") {
        return `<li>${block.text}</li>`;
      } else if (block.type === "ordered-list-item") {
        return `<li>${block.text}</li>`;
      } else {
        return `<p>${block.text}</p>`;
      }
    })
    .join("");
};

const OfferDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, isArabic } = useTranslation();

  // Use RTK Query to fetch offer
  const {
    data: offerResponse,
    isLoading: loading,
    error,
  } = useGetSpecialOfferQuery(id);
  const { data: settingsResponse } = useGetHomepageSettingsQuery();

  const offer = offerResponse?.offer || null;
  const logoUrl = settingsResponse?.settings?.branding?.logo || null;

  // Initialize AOS and track page visit
  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: "ease-in-out",
      once: true,
      offset: 100,
    });
  }, []);

  // Track offer page visit
  useEffect(() => {
    if (offer) {
      trackOfferPage(offer.id, offer.title);
    }
  }, [offer]);

  // Set page title and meta keywords
  useEffect(() => {
    if (offer) {
      // Set page title
      document.title = offer.title || "Special Offer";

      // Remove ALL existing meta keywords tags (in case HeadTags or other components added them)
      const existingMetaKeywords = document.querySelectorAll(
        'meta[name="keywords"]'
      );
      existingMetaKeywords.forEach((tag) => tag.remove());

      // Create new meta keywords tag with offer keywords
      if (offer.keywords || offer.title) {
        const metaKeywords = document.createElement("meta");
        metaKeywords.setAttribute("name", "keywords");

        // Combine offer title and keywords
        const keywords = offer.keywords
          ? `${offer.title}, ${offer.keywords}`
          : offer.title;

        metaKeywords.setAttribute("content", keywords);
        document.head.appendChild(metaKeywords);
      }
    }

    // Cleanup function
    return () => {
      document.title = "Silver Fort";
      // Remove meta keywords on unmount
      const metaKeywords = document.querySelectorAll('meta[name="keywords"]');
      metaKeywords.forEach((tag) => tag.remove());
    };
  }, [offer]);

  const handleBackToOffers = () => {
    navigate("/services");
  };

  const handleContactUs = () => {
    navigate("/#contact");
  };

  if (loading) {
    return (
      <div className="offer-detail-page">
        <div className="container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading offer details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !offer) {
    return (
      <div className="offer-detail-page">
        <div className="container">
          <div className="error-container">
            <h1>Offer Not Found</h1>
            <p>
              The offer you're looking for doesn't exist or has been removed.
            </p>
            <button className="btn" onClick={handleBackToOffers}>
              Back to Offers
            </button>
          </div>
        </div>
      </div>
    );
  }

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  return (
    <div className="offer-detail-page">
      <Header />
      <div className="container">
        {/* Main Content */}
        <div className="offer-main-content">
          <div
            className="offer-image-section"
            data-aos="fade-right"
            data-aos-delay="200"
          >
            {offer.image_url ? (
              <img
                src={offer.image_url}
                alt={offer.title}
                className="offer-hero-image"
                onError={(e) => {
                  e.target.style.display = "none";
                  if (e.target.nextSibling) {
                    e.target.nextSibling.style.display = "flex";
                  }
                }}
              />
            ) : null}
            <div
              className="offer-image-placeholder"
              style={{
                display: offer.image_url ? "none" : "flex",
              }}
            >
              <div className="placeholder-content">
                <div className="placeholder-icon">📸</div>
                <p>{t("common.noImageAvailable")}</p>
              </div>
            </div>
          </div>

          <div
            className="offer-details"
            data-aos="fade-left"
            data-aos-delay="300"
          >
            <h1 className="offer-title">
              {isArabic && offer.title_ar ? offer.title_ar : offer.title}
            </h1>
            <div className="offer-description">
              <div
                dangerouslySetInnerHTML={{
                  __html: parseDraftContent(
                    isArabic && offer.content_ar
                      ? offer.content_ar
                      : offer.content
                  ),
                }}
              />
            </div>

            <div className="offer-cta" data-aos="fade-up" data-aos-delay="400">
              <div className="cta-content">
                <h2>{t("offers.interestedInOffer")}</h2>
                <p>{t("offers.offerDescription")}</p>

                <div className="cta-buttons">
                  <button className="btn btn-primary" onClick={handleContactUs}>
                    {t("contact.contactUsNow")}
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={handleBackToOffers}
                  >
                    {t("offers.viewAll")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default OfferDetail;
